import {
  GeoSpatialFileType,
  getAllBoundariesApi,
  getAllFieldPolygonsApi,
  locutusCreateBoundaryApi,
  mapPlanAssetCreateError,
  type PlanAssetCreateErrorResult
} from 'api-validations/locutus'
import { useOrgCodeOrThrow } from 'shared-ui/active-operation'
import { useApiClient, useMutation, useQueryClient } from 'shared-ui/api-provider'
import { useMapContext } from 'shared-ui/map'
import type z from 'zod'

type BoundaryAssetGeoJSON = {
  fieldId: string
  name: string
}

type CreateBoundaryResponse = z.infer<typeof locutusCreateBoundaryApi.responseSchema>

/**
 * Mutation for creating a GeoJSON-based boundary asset
 */
export const useCreateFieldBoundary = (
  onSuccess?: (boundary?: { name: string; id: string }) => void
) => {
  const orgCode = useOrgCodeOrThrow()
  const apiClient = useApiClient()
  const queryClient = useQueryClient()
  const { map } = useMapContext()

  return useMutation<CreateBoundaryResponse[], PlanAssetCreateErrorResult, BoundaryAssetGeoJSON[]>({
    mutationFn: async (assets: BoundaryAssetGeoJSON[]) => {
      try {
        // This flow posts a GeoJSON object directly (bypassing the request schema,
        // whose `boundary` field expects a JSON string), so it can't go through
        // callApi/errorMap — map the error here to match the other create flows.
        return await Promise.all(
          assets.map(async ({ name, fieldId }) => {
            const response = await apiClient.post(locutusCreateBoundaryApi.pathFor({ orgCode }), {
              body: {
                fieldId: fieldId,
                name,
                type: GeoSpatialFileType.GeoJSON,
                boundary: map.drawTool.exportAsGeoJSON()
              }
            })

            return locutusCreateBoundaryApi.responseSchema.parse(response.body)
          })
        )
      } catch (e) {
        throw mapPlanAssetCreateError(e)
      }
    },
    onSuccess: (result) => {
      onSuccess?.(result[0]?.items[0])
      queryClient.invalidateQueries({
        queryKey: getAllBoundariesApi.queryKey({ orgCode })
      })
      setTimeout(() => {
        queryClient.invalidateQueries({
          queryKey: getAllFieldPolygonsApi.queryKey({ orgCode })
        })
      }, 3_000) // wait for backend processing, since these are saved asynchronously
    }
  })
}
